import React, { useContext, useEffect, useReducer } from "react";
import {
  Table, Label, Message, Icon, Loader, Segment, Dimmer, Pagination, Dropdown
} from "semantic-ui-react";
import { Link } from "react-router-dom";
import { searchVodafoneServices } from "../../libs/requests/vodafoneServiceSearch";
import { authContext } from "../../containers/AuthProvider";

const PAGE_SIZE = 10;

function statusColour(status) {
  switch (status?.toLowerCase()) {
    case "active":                return "green";
    case "provisioning":          return "blue";
    case "activeceasepending":    return "orange";
    case "ceasedwithsupplier":
    case "ceased":                return "grey";
    default:                      return "grey";
  }
}

function reducer(state, action) {
  switch (action.type) {
    case "SET_DATA":    return { ...state, services: action.services, totalRecords: action.totalRecords, isLoading: false, error: null };
    case "SET_LOADING": return { ...state, isLoading: true, error: null };
    case "SET_ERROR":   return { ...state, isLoading: false, error: action.error };
    case "SET_PAGE":    return { ...state, page: action.page };
    default:            return state;
  }
}

export default function VodafoneServicesTable({ searchTerm = "", filterByPartner = "", partners = [], onAssign }) {
  const [state, dispatch] = useReducer(reducer, {
    services: [], totalRecords: 0, isLoading: true, error: null, page: 1
  });
  const { permissions } = useContext(authContext);

  useEffect(() => {
    dispatch({ type: "SET_PAGE", page: 1 });
    load(1);
  }, [searchTerm, filterByPartner]);

  async function load(page = state.page) {
    dispatch({ type: "SET_LOADING" });
    try {
      const data = await searchVodafoneServices(searchTerm, page - 1, PAGE_SIZE, filterByPartner);
      dispatch({ type: "SET_DATA", services: data.services, totalRecords: data.totalRecords });
    } catch {
      dispatch({ type: "SET_ERROR", error: "Failed to load Vodafone services." });
    }
  }

  function handlePageChange(_, { activePage }) {
    dispatch({ type: "SET_PAGE", page: activePage });
    load(activePage);
  }

  if (state.isLoading) {
    return (
      <Segment>
        <Dimmer active inverted><Loader>Loading Vodafone services…</Loader></Dimmer>
      </Segment>
    );
  }

  if (state.error || !state.services.length) {
    return (
      <Message info>
        <Icon name="info circle" /> No Vodafone services found.
      </Message>
    );
  }

  const totalPages = Math.ceil(state.totalRecords / PAGE_SIZE) || 1;

  return (
    <>
      <Table celled>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>Reference</Table.HeaderCell>
            <Table.HeaderCell>Service Status</Table.HeaderCell>
            <Table.HeaderCell>CLI</Table.HeaderCell>
            <Table.HeaderCell>Post Code</Table.HeaderCell>
            <Table.HeaderCell>Customer Ref</Table.HeaderCell>
            <Table.HeaderCell>Product</Table.HeaderCell>
            <Table.HeaderCell>Care Level</Table.HeaderCell>
            {permissions?.admin && <Table.HeaderCell>Assigned To</Table.HeaderCell>}
            {permissions?.admin && <Table.HeaderCell>Actions</Table.HeaderCell>}
          </Table.Row>
        </Table.Header>

        <Table.Body>
          {state.services.map((svc) => (
            <Table.Row key={svc.reference}>
              <Table.Cell>
                <Link to={`/Services/${svc.reference}?supplier=Vodafone`}>
                  {svc.reference}
                </Link>
              </Table.Cell>
              <Table.Cell>
                <Label color={statusColour(svc.serviceStatus)}>{svc.serviceStatus}</Label>
              </Table.Cell>
              <Table.Cell>{svc.cli ?? "—"}</Table.Cell>
              <Table.Cell>{svc.postCode ?? "—"}</Table.Cell>
              <Table.Cell>{svc.customerReference ?? "—"}</Table.Cell>
              <Table.Cell>{svc.product ?? "—"}</Table.Cell>
              <Table.Cell>{svc.careLevel ?? "—"}</Table.Cell>
              {permissions?.admin && (
                <Table.Cell>{svc.assignedTo ?? "—"}</Table.Cell>
              )}
              {permissions?.admin && onAssign && (
                <Table.Cell>
                  <Dropdown text="Assign" fluid button className="icon" scrolling
                    style={{ minWidth: "150px", position: "relative" }}>
                    <Dropdown.Menu style={{
                      maxHeight: "300px", overflowY: "auto", minWidth: "250px",
                      position: "absolute", left: 0, zIndex: 1001, marginTop: "5px"
                    }}>
                      {partners.map((p) => (
                        <Dropdown.Item key={p.Partner}
                          onClick={() => onAssign(svc.reference, p.Partner, "VODAFONE")}
                          style={{ padding: "10px 15px" }}>
                          {p.Partner}
                        </Dropdown.Item>
                      ))}
                    </Dropdown.Menu>
                  </Dropdown>
                </Table.Cell>
              )}
            </Table.Row>
          ))}
        </Table.Body>
      </Table>

      {totalPages > 1 && (
        <Pagination activePage={state.page} totalPages={totalPages}
          onPageChange={handlePageChange}
          firstItem={null} lastItem={null}
          prevItem={{ content: <Icon name="angle left" />, icon: true }}
          nextItem={{ content: <Icon name="angle right" />, icon: true }}
        />
      )}
    </>
  );
}
